import { RelationObjectType } from "@enums/relations.enum";
import { ICompanyTag, IDealTag, IPeopleTag } from "@interfaces/tag.interface";

/** Field type of an attribute — string values must match the backend exactly. */
export enum AttributeType {
  // Common
  TEXT = "Text",
  PERCENTAGE = "Percentage",
  NUMBER = "Number",
  CURRENCY = "Currency",
  DROPDOWN = "Select",
  MULTISELECT = "Multi Select",
  // Wire format, not a label — must match the backend's `Attribute.Type` value exactly.
  RELATION = "Meridian Record",

  // Deals
  MULTIPLE = "Multiple",
  CREDIT = "Credit Pricing",
  DATE = "Date",
  SCOUT_EXTRACT = "Scout Extract",
  SCOUT_SCORE = "Scout Score",
  SCOUT_CLASSIFY = "Scout Classify",

  // Companies
  YEAR = "Year",
  NUMBER_NOT_NEGATIVE = "Number (not negative)",
  URL = "URL",
  LOCATION = "Location",
}

/** Target key used by the search UI; maps 1:1 to a tagging options group. */
export type RelationTargetType = "deals" | "companies" | "people";

/** Django content-type identifiers used by the backend. */
export type RelationContentType = "deals.deal" | "companies.company" | "people.person";

/** A tagged record: `id` is the relation, `object` the record. Shaped like `ITagResponse`. */
export interface IRelationObject {
  id: string;
  object_type: RelationContentType;
  object: IDealTag | ICompanyTag | IPeopleTag;
}

/** Payload shape the backend accepts for a relation. */
export interface IRelationObjectPayload {
  content_type: RelationContentType;
  object_id: string;
}

export interface IAttributeDropdownOption {
  id: string;
  title: string;
  order: number;
  is_default?: boolean;
  company_types?: string[];
  icon?: string;
}

export interface IAttributeApplicableModel {
  order: number;
  content_type: RelationObjectType;
}

export interface IAttributeCompanyType {
  type: string;
  order: number;
}

export interface IAttribute {
  id: string;
  title: string;
  description: string;
  type: AttributeType;
  formula: string;
  dropdown_options: IAttributeDropdownOption[];
  humanized_formula: string | null;
  is_enrichable: boolean;
  applicable_models: IAttributeApplicableModel[];
  company_types: IAttributeCompanyType[];
  /** RELATION only: content types a user may tag here. */
  relation_content_types?: RelationContentType[];
  /** RELATION only: restricts the field to one record. */
  is_single_select?: boolean;
}

/** Form-side representation. For dropdown types, `value` holds comma-joined option ids. */
export interface IAttributeFieldValue {
  attribute: string;
  value: string;
  prefix?: string;
  selected_options?: string[];
  /** RELATION only: the tagged records, as held while editing. */
  relations?: IRelationObject[];
}

/** The form shape prepared for the API: `relations` reduced to write pairs. */
export type IAttributeFieldValuePayload = Omit<IAttributeFieldValue, "relations"> & {
  relations?: IRelationObjectPayload[];
};

/** Read shape returned by the API alongside an entity. */
export interface IAttributeValue {
  attribute: string;
  id: string;
  value: string;
  formatted_value: string;
  selected_options: string[];
  /** RELATION only: the tagged records. */
  relations?: IRelationObject[];
  prefix: string;
}
